import React, { useState, useMemo } from 'react';
import {
  Box,
  Button,
  Stack,
  Tabs,
  TabsList,
  TabsTab,
  TabsPanel,
  Spinner,
  Text,
  Grid,
} from '@customerio/pluma-components/react';
import { themeVars } from '@customerio/pluma-components/css/theme-tokens';
import JsonView from '@uiw/react-json-view';
import ReactDiffViewer from 'react-diff-viewer-continued';
import { servicesQuery } from '@/services/api';
import { useSafeNavigate } from '@/services/router/safe-navigate';
import type { AuditLogEntry } from '../types';
import { diffViewerStyles } from './diffTheme';
import { formatAttributeName, FriendlyJSONDisplay } from '../utils/formatting';
import { computeSimplifiedDiff, extractChangedFields } from '../utils/diffing';

interface AuditLogExpandedDetailsProps {
  log: AuditLogEntry;
  context: 'account' | 'workspace';
  contextId: number;
  logs?: AuditLogEntry[];
}

export const AuditLogExpandedDetails: React.FC<AuditLogExpandedDetailsProps> = ({
  log,
  context,
  contextId,
  logs = [],
}) => {
  const safeNavigate = useSafeNavigate();
  const [copyLabel, setCopyLabel] = useState('Copy Data');
  const [idCopied, setIdCopied] = useState(false);
  // Only show diff tab for "Updated" actions
  const canShowDiff = log.action.toLowerCase() === 'updated';

  // Find previous log in memory - compute early so we can use it in initial state
  const findPreviousLogInMemory = (logsArray: AuditLogEntry[]): AuditLogEntry | undefined => {
    let previousLog =
      log.version > 1
        ? logsArray.find(
            (l) => l.object_type === log.object_type && l.object_id === log.object_id && l.version === log.version - 1,
          )
        : undefined;

    if (previousLog == null) {
      previousLog = logsArray
        .filter(
          (l) =>
            l.object_type === log.object_type &&
            l.object_id === log.object_id &&
            l.id !== log.id &&
            (l.timestamp < log.timestamp ||
              (l.timestamp === log.timestamp &&
                l.action.toLowerCase() === 'created' &&
                log.action.toLowerCase() === 'updated')),
        )
        .sort((a, b) => {
          if (b.timestamp !== a.timestamp) {
            return b.timestamp - a.timestamp;
          }
          if (a.action.toLowerCase() === 'created' && b.action.toLowerCase() !== 'created') {
            return -1;
          }
          if (b.action.toLowerCase() === 'created' && a.action.toLowerCase() !== 'created') {
            return 1;
          }
          return 0;
        })[0];
    }

    return previousLog;
  };

  const previousLogInMemory = findPreviousLogInMemory(logs);

  // Set default tab to 'diff' when multiple tabs exist, otherwise 'raw'
  const [activeTab, setActiveTab] = useState(canShowDiff ? 'diff' : 'raw');
  // Initialize shouldFetchPrevious to true when canShowDiff is true and previousLogInMemory is null
  // This ensures the diff view fetches data on initial render when needed
  const [shouldFetchPrevious, setShouldFetchPrevious] = useState(canShowDiff && previousLogInMemory == null);

  // Show both data and meta fields in Raw tab - rest is in table columns
  // Preserve arrays and objects - don't convert arrays to empty objects
  const logData = log.data != null ? log.data : null;
  const logMeta = log.meta != null ? log.meta : null;
  // Combine data and meta into a single object for display
  // Always include both keys for consistent structure, even if empty
  const logPayload: Record<string, unknown> = {
    data: logData,
    meta: logMeta,
  };

  const handleCopyJSON = async () => {
    if (navigator.clipboard == null) {
      setCopyLabel('Copy not supported');
      setTimeout(() => setCopyLabel('Copy Data'), 2000);
      return;
    }
    try {
      // Copy only the data, not the meta
      // Preserve arrays and other types - use log.data directly
      // JSON.stringify(null) returns 'null', preserving null values correctly
      const dataString = JSON.stringify(logData, null, 2);
      await navigator.clipboard.writeText(dataString);
      setCopyLabel('Copied!');
      setTimeout(() => setCopyLabel('Copy Data'), 2000);
    } catch (error) {
      setCopyLabel('Copy failed');
      setTimeout(() => setCopyLabel('Copy Data'), 2000);
    }
  };

  const handleCopyId = async () => {
    if (navigator.clipboard == null) {
      return;
    }
    try {
      // Copy link to audit logs page with this specific log highlighted
      const baseUrl = context === 'account' ? `/settings/audit-logs` : `/workspaces/${contextId}/settings/audit-logs`;
      const url = new URL(baseUrl, window.location.origin);
      url.searchParams.set('audit_log_id', log.id.toString());
      await navigator.clipboard.writeText(url.toString());
      setIdCopied(true);
      setTimeout(() => setIdCopied(false), 2000);
    } catch (error) {
      setIdCopied(false);
    }
  };

  const handleFilterByObject = () => {
    const baseUrl = context === 'account' ? `/settings/audit-logs` : `/workspaces/${contextId}/settings/audit-logs`;
    const url = new URL(baseUrl, window.location.origin);
    url.searchParams.set('object_type', log.object_type);
    url.searchParams.set('object_id', log.object_id);
    safeNavigate(`${url.pathname}${url.search}`);
  };

  const previousVersionQuery =
    context === 'account'
      ? servicesQuery.getAccountAuditLogs.useQuery({
          queryKey: ['previousAuditLog', 'account', contextId, log.object_type, log.object_id, log.version - 1],
          queryData: {
            params: { account_id: contextId },
            query: {
              object_type: log.object_type,
              object_id: log.object_id,
              limit: 20,
              sort_order: 'desc',
            },
          },
          enabled: canShowDiff && previousLogInMemory == null && shouldFetchPrevious,
        })
      : servicesQuery.getWorkspaceAuditLogs.useQuery({
          queryKey: ['previousAuditLog', 'workspace', contextId, log.object_type, log.object_id, log.version - 1],
          queryData: {
            params: { environment_id: contextId },
            query: {
              object_type: log.object_type,
              object_id: log.object_id,
              limit: 20,
              sort_order: 'desc',
            },
          },
          enabled: canShowDiff && previousLogInMemory == null && shouldFetchPrevious,
        });

  let previousLogFromAPI =
    log.version > 1 ? previousVersionQuery.data?.body?.data?.find((l) => l.version === log.version - 1) : undefined;

  if (previousLogFromAPI == null && previousVersionQuery.data?.body?.data != null) {
    previousLogFromAPI = previousVersionQuery.data.body.data
      .filter(
        (l) =>
          l.id !== log.id &&
          (l.timestamp < log.timestamp ||
            (l.timestamp === log.timestamp &&
              l.action.toLowerCase() === 'created' &&
              log.action.toLowerCase() === 'updated')),
      )
      .sort((a, b) => {
        if (b.timestamp !== a.timestamp) {
          return b.timestamp - a.timestamp;
        }
        if (a.action.toLowerCase() === 'created' && b.action.toLowerCase() !== 'created') {
          return -1;
        }
        if (b.action.toLowerCase() === 'created' && a.action.toLowerCase() !== 'created') {
          return 1;
        }
        return 0;
      })[0];
  }

  const previousLog = previousLogInMemory ?? previousLogFromAPI;

  // Use stable data references directly to avoid dependency on computed previousLog variable
  const previousLogData = previousLogInMemory?.data ?? previousLogFromAPI?.data;

  // Memoize expensive diffing operations to avoid recalculating on every render
  // Use previousLogData and log.data directly in dependencies to avoid new object creation
  const { oldChanged, newChanged } = useMemo(
    () =>
      extractChangedFields(
        (previousLogData ?? {}) as Record<string, unknown>,
        (log.data ?? {}) as Record<string, unknown>,
      ),
    [previousLogData, log.data],
  );

  const simplifiedChanges = useMemo(
    () =>
      canShowDiff && previousLogData != null
        ? computeSimplifiedDiff(
            (previousLogData ?? {}) as Record<string, unknown>,
            (log.data ?? {}) as Record<string, unknown>,
          )
        : [],
    [canShowDiff, previousLogData, log.data],
  );

  const handleTabChange = (value: string | number) => {
    const tabValue = String(value);
    setActiveTab(tabValue);
    // Trigger fetch when diff or changes tab is clicked and we don't have the previous log
    if ((tabValue === 'diff' || tabValue === 'changes') && previousLogInMemory == null) {
      setShouldFetchPrevious(true);
    }
  };

  return (
    <Box p="300" backgroundColor="subtle" style={{ borderRadius: themeVars.radius['4'] }}>
      {/* @ts-expect-error - Pluma Tabs component has complex polymorphic types */}
      <Tabs activeTab={activeTab} onChange={handleTabChange}>
        <TabsList>
          {canShowDiff ? <TabsTab value="diff">Diff</TabsTab> : null}
          {canShowDiff ? (
            <TabsTab value="changes">
              <Box display="flex" alignItems="center" gap="100">
                <Text>Changes</Text>
                {simplifiedChanges.length > 0 ? (
                  <Box
                    display="inline-flex"
                    alignItems="center"
                    justifyContent="center"
                    backgroundColor="accent"
                    style={{
                      minWidth: '20px',
                      height: '20px',
                      borderRadius: themeVars.radius['6'],
                    }}
                  >
                    <Text size="xs" color="invert" weight="semibold">
                      {simplifiedChanges.length}
                    </Text>
                  </Box>
                ) : null}
              </Box>
            </TabsTab>
          ) : null}
          <TabsTab value="raw">Raw</TabsTab>
        </TabsList>

        <TabsPanel value="raw">
          <Box style={{ position: 'relative' }}>
            <Box style={{ position: 'absolute', top: '12px', right: '28px', zIndex: 1 }}>
              <Stack gap="100">
                {log.object_id != null && log.object_id !== '' ? (
                  <Button
                    variant="secondary"
                    size="sm"
                    icon="filter"
                    onClick={handleFilterByObject}
                    style={{ minWidth: '168px' }}
                  >
                    Filter by this object
                  </Button>
                ) : null}
                <Button
                  variant="secondary"
                  size="sm"
                  icon={copyLabel === 'Copied!' ? 'check' : 'copy-to-clipboard'}
                  onClick={handleCopyJSON}
                >
                  {copyLabel}
                </Button>
                <Button variant="secondary" size="sm" icon={idCopied ? 'check' : 'link'} onClick={handleCopyId}>
                  {idCopied ? 'Copied!' : 'Copy Link'}
                </Button>
              </Stack>
            </Box>
            <Box
              pt="300"
              pb="300"
              pr="800"
              pl="300"
              backgroundColor="base"
              overflowY="auto"
              style={{
                minHeight: '200px', // Ensure minimum height to accommodate buttons (3 buttons + gaps + padding)
                maxHeight: '600px',
                border: `${themeVars.border['width-0125']} solid ${themeVars.color['border-base']}`,
                borderRadius: themeVars.radius['4'],
              }}
            >
              <Box className="audit-log-json-view">
                <JsonView
                  value={logPayload as object}
                  collapsed={false}
                  displayDataTypes={false}
                  displayObjectSize={false}
                  enableClipboard={false}
                  style={
                    {
                      fontSize: '13px',
                      fontFamily: 'monospace',
                      backgroundColor: themeVars.color['surface-base'],
                      wordBreak: 'break-word',
                      overflowWrap: 'break-word',
                      '--w-rjv-color': themeVars.color['text-base'],
                      '--w-rjv-key-string': themeVars.color['text-accent'],
                      '--w-rjv-background-color': themeVars.color['surface-base'],
                      '--w-rjv-line-color': themeVars.color['border-subtle'],
                      '--w-rjv-arrow-color': themeVars.color['text-subtle'],
                      '--w-rjv-info-color': themeVars.color['text-subtle'],
                      '--w-rjv-type-string-color': themeVars.color['success-text'],
                      '--w-rjv-type-int-color': 'var(--pluma-color-warning-text)',
                      '--w-rjv-type-float-color': 'var(--pluma-color-warning-text)',
                      '--w-rjv-type-bigint-color': 'var(--pluma-color-warning-text)',
                      '--w-rjv-type-boolean-color': themeVars.color['danger-text'],
                      '--w-rjv-type-null-color': themeVars.color['text-subtle'],
                      '--w-rjv-type-undefined-color': themeVars.color['text-subtle'],
                    } as React.CSSProperties
                  }
                />
              </Box>
            </Box>
          </Box>
        </TabsPanel>

        {canShowDiff ? (
          <TabsPanel value="diff">
            <Box>
              {previousVersionQuery.isLoading ? (
                <Box
                  p="400"
                  textAlign="center"
                  backgroundColor="base"
                  style={{
                    border: `${themeVars.border['width-0125']} solid ${themeVars.color['border-base']}`,
                    borderRadius: themeVars.radius['4'],
                  }}
                >
                  <Spinner />
                  <Box mt="200">
                    <Text>Loading previous version...</Text>
                  </Box>
                </Box>
              ) : previousLog != null ? (
                <Box
                  overflow="auto"
                  style={{
                    maxHeight: '500px',
                    border: `${themeVars.border['width-0125']} solid ${themeVars.color['border-base']}`,
                    borderRadius: themeVars.radius['4'],
                  }}
                >
                  <ReactDiffViewer
                    oldValue={JSON.stringify(oldChanged, null, 2)}
                    newValue={JSON.stringify(newChanged, null, 2)}
                    splitView={false}
                    hideLineNumbers={false}
                    showDiffOnly={true}
                    useDarkTheme={false}
                    styles={diffViewerStyles}
                    disableWordDiff={true}
                  />
                </Box>
              ) : (
                <Box
                  p="400"
                  textAlign="center"
                  backgroundColor="base"
                  style={{
                    border: `${themeVars.border['width-0125']} solid ${themeVars.color['border-base']}`,
                    borderRadius: themeVars.radius['4'],
                  }}
                >
                  <Text>Previous version (v{log.version - 1}) not found. It may have been purged or filtered out.</Text>
                </Box>
              )}
            </Box>
          </TabsPanel>
        ) : null}

        {canShowDiff ? (
          <TabsPanel value="changes">
            <Box>
              {previousVersionQuery.isLoading ? (
                <Box
                  p="400"
                  style={{
                    borderRadius: themeVars.radius['4'],
                    border: `${themeVars.border['width-0125']} solid ${themeVars.color['border-subtle']}`,
                    backgroundColor: themeVars.color['surface-base'],
                  }}
                  textAlign="center"
                >
                  <Spinner />
                  <Box mt="200">
                    <Text>Loading previous version...</Text>
                  </Box>
                </Box>
              ) : previousLog != null ? (
                <Box>
                  {simplifiedChanges.length === 0 ? (
                    <Box
                      p="400"
                      style={{
                        borderRadius: themeVars.radius['4'],
                        border: `${themeVars.border['width-0125']} solid ${themeVars.color['border-subtle']}`,
                        backgroundColor: themeVars.color['surface-base'],
                      }}
                      textAlign="center"
                    >
                      <Text>No changes detected.</Text>
                    </Box>
                  ) : (
                    <Grid
                      columns={
                        simplifiedChanges.length === 1
                          ? '1fr'
                          : simplifiedChanges.length === 2
                          ? 2
                          : 'repeat(auto-fill, minmax(300px, 1fr))'
                      }
                      gap="150"
                    >
                      {simplifiedChanges.map((change, index) => {
                        // Check if this is user_flags or account_flags - make it span full width
                        const fieldLower = change.field.toLowerCase();
                        const isFlagsField =
                          fieldLower === 'user_flags' ||
                          fieldLower === 'userflags' ||
                          fieldLower === 'account_flags' ||
                          fieldLower === 'accountflags';

                        return (
                          <Box
                            key={`${change.field}-${index}`}
                            p="300"
                            style={{
                              borderRadius: themeVars.radius['4'],
                              border: `${themeVars.border['width-0125']} solid ${themeVars.color['border-subtle']}`,
                              gridColumn: isFlagsField ? '1 / -1' : undefined,
                              borderLeft: `${themeVars.border['width-050']} solid ${
                                change.type === 'added'
                                  ? themeVars.color['success-base']
                                  : change.type === 'removed'
                                  ? themeVars.color['danger-base']
                                  : themeVars.color['border-subtle']
                              }`,
                            }}
                          >
                            <Stack gap="100">
                              <Box display="flex" alignItems="center" gap="100">
                                {change.type === 'added' ? (
                                  <>
                                    <Box
                                      style={{
                                        width: '8px',
                                        height: '8px',
                                        borderRadius: '50%',
                                        backgroundColor: themeVars.color['success-base'],
                                      }}
                                    />
                                    <Text size="s" weight="medium">
                                      {formatAttributeName(change.field)}
                                    </Text>
                                    <Text size="s" color="subtle">
                                      was added
                                    </Text>
                                  </>
                                ) : change.type === 'removed' ? (
                                  <>
                                    <Box
                                      style={{
                                        width: '8px',
                                        height: '8px',
                                        borderRadius: '50%',
                                        backgroundColor: themeVars.color['danger-base'],
                                      }}
                                    />
                                    <Text size="s" weight="medium">
                                      {formatAttributeName(change.field)}
                                    </Text>
                                    <Text size="s" color="subtle">
                                      was removed
                                    </Text>
                                  </>
                                ) : (
                                  <>
                                    <Box
                                      backgroundColor="caution"
                                      style={{
                                        width: '8px',
                                        height: '8px',
                                        borderRadius: '50%',
                                      }}
                                    />
                                    <Text size="s" weight="medium">
                                      {formatAttributeName(change.field)}
                                    </Text>
                                  </>
                                )}
                              </Box>
                              {change.type === 'changed' ? (
                                <Stack gap="050">
                                  <Box
                                    p="150"
                                    style={{
                                      borderRadius: themeVars.radius['4'],
                                      backgroundColor: themeVars.color['danger-subtle'],
                                      borderLeft: `${themeVars.border['width-050']} solid ${themeVars.color['danger-base']}`,
                                      width: '100%',
                                      minWidth: 0,
                                      overflow: 'hidden',
                                    }}
                                  >
                                    <FriendlyJSONDisplay
                                      value={change.oldValue}
                                      textColor={themeVars.color['danger-text']}
                                      parentKey={change.field}
                                    />
                                  </Box>
                                  <Box
                                    p="150"
                                    style={{
                                      borderRadius: themeVars.radius['4'],
                                      backgroundColor: themeVars.color['success-subtle'],
                                      borderLeft: `${themeVars.border['width-050']} solid ${themeVars.color['success-base']}`,
                                      width: '100%',
                                      minWidth: 0,
                                      overflow: 'hidden',
                                    }}
                                  >
                                    <FriendlyJSONDisplay
                                      value={change.newValue}
                                      textColor={themeVars.color['success-text']}
                                      parentKey={change.field}
                                    />
                                  </Box>
                                </Stack>
                              ) : change.type === 'added' ? (
                                <Box
                                  p="200"
                                  style={{
                                    borderRadius: themeVars.radius['4'],
                                    backgroundColor: themeVars.color['success-subtle'],
                                    borderLeft: `${themeVars.border['width-050']} solid ${themeVars.color['success-base']}`,
                                    width: '100%',
                                    minWidth: 0,
                                    overflow: 'hidden',
                                  }}
                                >
                                  <FriendlyJSONDisplay
                                    value={change.newValue}
                                    textColor={themeVars.color['success-text']}
                                    parentKey={change.field}
                                  />
                                </Box>
                              ) : (
                                <Box
                                  p="200"
                                  style={{
                                    borderRadius: themeVars.radius['4'],
                                    backgroundColor: themeVars.color['danger-subtle'],
                                    borderLeft: `${themeVars.border['width-050']} solid ${themeVars.color['danger-base']}`,
                                    width: '100%',
                                    minWidth: 0,
                                    overflow: 'hidden',
                                  }}
                                >
                                  <FriendlyJSONDisplay
                                    value={change.oldValue}
                                    textColor={themeVars.color['danger-text']}
                                    parentKey={change.field}
                                  />
                                </Box>
                              )}
                            </Stack>
                          </Box>
                        );
                      })}
                    </Grid>
                  )}
                </Box>
              ) : (
                <Box
                  p="400"
                  style={{
                    borderRadius: themeVars.radius['4'],
                    border: `${themeVars.border['width-0125']} solid ${themeVars.color['border-subtle']}`,
                    backgroundColor: themeVars.color['surface-base'],
                  }}
                  textAlign="center"
                >
                  <Text>Previous version (v{log.version - 1}) not found. It may have been purged or filtered out.</Text>
                </Box>
              )}
            </Box>
          </TabsPanel>
        ) : null}
      </Tabs>
    </Box>
  );
};
